/**
 * Volume slider position ↔ amplitude mapping (RFC: timeline-volume).
 *
 * The user sees a 0–200% slider with a tick at 100% (= original level).
 * The stored `volume` field is a linear amplitude multiplier applied
 * directly by the backend native ffmpeg `volume` filter and by WebAudio's
 * GainNode.
 *
 * Mapping:
 *   - position ≤ 1.0 → amplitude = position  (linear attenuation, unchanged)
 *   - position > 1.0 → amplitude = 10 ** ((position - 1) * MAX_BOOST_DB / 20)
 *     so position = 2.0 corresponds to MAX_BOOST_DB above 0 dB.
 *
 * Concretely with MAX_BOOST_DB = 24:
 *   position 1.0 → amplitude 1.0   (0 dB,   original)
 *   position 1.5 → amplitude ≈ 3.98 (+12 dB)
 *   position 2.0 → amplitude ≈ 15.85 (+24 dB)
 */

export const MAX_VOLUME_POSITION = 2
export const MAX_BOOST_DB = 24
export const MAX_VOLUME_AMPLITUDE = 10 ** (MAX_BOOST_DB / 20)

export function positionToAmplitude(position: number): number {
  if (!Number.isFinite(position) || position <= 0) return 0
  if (position <= 1) return position
  const clampedPos = Math.min(MAX_VOLUME_POSITION, position)
  return 10 ** ((clampedPos - 1) * (MAX_BOOST_DB / 20))
}

export function amplitudeToPosition(amplitude: number): number {
  if (!Number.isFinite(amplitude) || amplitude <= 0) return 0
  if (amplitude <= 1) return amplitude
  const clampedAmp = Math.min(MAX_VOLUME_AMPLITUDE, amplitude)
  return 1 + Math.log10(clampedAmp) / (MAX_BOOST_DB / 20)
}

export function amplitudeToDbLabel(amplitude: number): string {
  if (!Number.isFinite(amplitude) || amplitude <= 0) return '-inf dB'
  const db = 20 * Math.log10(amplitude)
  const rounded = Math.abs(db) < 0.05 ? 0 : db
  return `${rounded > 0 ? '+' : ''}${rounded.toFixed(1)} dB`
}
